/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import LoadingOverlayWrapper from 'react-loading-overlay-ts';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import TuneIcon from '@mui/icons-material/Tune';
import { useNavigate, useParams } from 'react-router-dom';
import {v4 as uuidv4} from 'uuid'
import { styles } from './styles';

import { ViewHeader } from '@components/ViewHeader';
import MapView, { mapStyles } from '@components/MapView/MapView';
import { MAP_LAYERS } from '@components/FieldMapOptionsModal/FieldMapOptionsModal';
import { ToastType } from '@components/ui/Toast';
import { useAppTheme } from '@hooks/useAppTheme';
import { useGetFieldTables } from '@hooks/useGetFieldTables';
import { useGetSoilTable } from '@hooks/useGetSoilTable';
import { useGetFieldNotes } from '@hooks/useGetFieldNotes';
import { useFieldNotesMarkers } from '@hooks/useFieldNoteMarkers';
import useImagery from '@hooks/useImagey';
import { useCreateFieldNote } from '@hooks/useCreateFieldNote';
import { useGetCurrentLocation } from '@hooks/useGetCurrentLocation';
import { useToast } from '@hooks/app/useToast';
import { Position } from '@mytypes/map';
import { createFieldVisitNoteWithDexie } from '@services/local';
import { RootState } from '@state-mgmt/store';
import { setShowAppNavigator } from '@state-mgmt/slices/appSlice';
import { getFieldCenterPoint } from '@utils/getFieldCenterPoint';
import UploadPhoto from './UploadPhoto';
import config from '../../../config';
import useGetSteps from './useGetSteps';
import { useFieldMapOptionsModal } from '@hooks/modals/useFieldMapOptionsModal';
import { selectFieldVisitWizardMapStyle } from '@state-mgmt/slices/visitSlice';

const API_DATE_FORMAT = 'MM/DD/YYYY HH:mm:ss [GMT]ZZ';

const FieldNoteCreate = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const showToast = useToast();

  const { growerId, fieldId, protocolId } = useParams();
  const theme = useAppTheme({});
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const { currentPosition, online, simulateGPS, trialId, clientId } = useSelector((state: RootState) => state.app);
  const { trialProducts } = useSelector((state: RootState) => state.trial);
  const getCurrentLocation = useGetCurrentLocation();

  const [files, setFiles] = useState<File[] | null>(null);
  const [markers, setMarkers] = useState<any[]>([]);
  const [fieldBoundary, setFieldBoundary] = useState<any | null>(null);
  const [trialAreaGeoJson, setTrialAreaGeoJson] = useState<any | undefined>();
  const [zoneGeojson, setZoneGeojson] = useState<any | undefined>();
  const [soilGeojson, setSoilGeojson] = useState<any | undefined>();
  const [treatmentZoneLegends, setTreatmentZoneLegends] = useState<{ label: string; color: string }[]>([]);
  const [latitude, setLatitude] = useState<number>(0);
  const [longitude, setLongitude] = useState<number>(0);
  const [note, setNote] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [stepId, setStepId] = useState<string | null>(null);
  const [geoLocationPermissionsAlertOpen, setGeoLocationPermissionsAlertOpen] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showNetworkError, setShowNetworkError] = useState(false);

  const [getFieldTablesState, getFieldTables] = useGetFieldTables();
  const [, getSoilTable] = useGetSoilTable();
  const [createFieldNoteState, createFieldNote] = useCreateFieldNote();
  const [getFieldNotesState] = useGetFieldNotes((fieldId || null) as string | null, protocolId as string | null, trialId || null);
  const fieldNotesMarkers = useFieldNotesMarkers(getFieldNotesState.data, 'fieldNotePopup');

  const [localNoteId] = useState<string>(uuidv4())

  const { steps } = useGetSteps({ protocolId, trialId });

  useEffect(() => {
    if (createFieldNoteState.error) {
        if (createFieldNoteState.isNetworkError) {
            setShowNetworkError(true)
        } else {
            setShowError(true);
        }
    }
  }, [createFieldNoteState.error]);

  useEffect(() => {
    dispatch(setShowAppNavigator(false));
    if (!simulateGPS) {
      getCurrentLocation();
    }
  }, [simulateGPS, dispatch]);

  useEffect(() => {
    if (trialId && protocolId && fieldId) {
      getFieldTables(trialId, protocolId, fieldId, true, false);
    }
  }, [fieldId, protocolId, trialId]);

  useEffect(() => {
    if (currentPosition) {
      setLatitude(currentPosition.latitude);
      setLongitude(currentPosition.longitude);
    }
  }, [currentPosition]);

  useEffect(() => {
    if (getFieldTablesState.data) {
      if (getFieldTablesState.data.field) {
        const data = getFieldTablesState.data.field;
        setFieldBoundary(data);
        if (!currentPosition && !simulateGPS) {
          setGeoLocationPermissionsAlertOpen(true);
        }
      }

      if (getFieldTablesState.data.area) {
        setTrialAreaGeoJson(getFieldTablesState.data.area);
      }

      if (getFieldTablesState.data.treatmentsZones) {
        let treatmentZones: { label: string; productId: string; color: string }[] = [];
        getFieldTablesState.data.treatmentsZones.features.forEach((treatmentZone: any) => {
          const zoneProductId = treatmentZone.properties.product_id;
          if (zoneProductId && !treatmentZones.some(tz => tz.productId === zoneProductId)) {
            const product = trialProducts.find(trialProduct => trialProduct.id === zoneProductId);
            if (product) {
              treatmentZones = [...treatmentZones, { label: product.name, color: product.color, productId: product.id }];
            }
          }
        });
        setTreatmentZoneLegends(treatmentZones);
        setZoneGeojson(getFieldTablesState.data.treatmentsZones);
      }

      if (getFieldTablesState.data.soil) {
        setSoilGeojson(getFieldTablesState.data.soil);
      } else if (getFieldTablesState.data.boundaryTableGuid) {
        getSoilTable(getFieldTablesState.data.boundaryTableGuid).then(response => {
          setSoilGeojson(response.soil);
        });
      }
    }
  }, [getFieldTablesState.data, trialProducts, currentPosition, simulateGPS]);

  useEffect(() => {
    if (createFieldNoteState.isSuccess) {
      handleGoBack();
    }
  }, [createFieldNoteState.isSuccess]);

  const hasLocation: boolean = useMemo(() => !!markers.length, [markers]);

  const handleGoBack = () => {
    dispatch(setShowAppNavigator(true));
    navigate(`/field-notes/${fieldId}/${protocolId}/${growerId}`);
  };

  const onMarkerDragEnd = (event: any, newPosition: Position) => {
    setLatitude(newPosition.latitude);
    setLongitude(newPosition.longitude);
    setMarkers([{ latitude: newPosition.latitude, longitude: newPosition.longitude, draggable: true }]);
  };

  const onUpdateNote = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (currentPosition) {
      setMarkers([{ ...currentPosition, draggable: true }]);
    }
    setNote(event.target.value);
  };

  const onClearNote = () => {
    setNote('');
    setMarkers([]);
  };

  const onSaveNote = () => {
    if (!note.match(new RegExp(config.REACT_APP_NOTE_REGEX))) {
      showToast({ type: ToastType.ERROR, children: 'Invalid characters in description' });
      return;
    }

    if (!trialId) return;

    const createSyncLaterNote = () => {
        createFieldVisitNoteWithDexie({
        localId: localNoteId,
        files: files || [],
        protocolId: protocolId || '',
        date: dayjs(new Date()).format(API_DATE_FORMAT),
        note: note || '',
        latitude: latitude || 0,
        longitude: longitude || 0,
        fieldId: fieldId || '',
        zoneId: null,
        stepId,
        title: title ? title : null
      });

      setShowNetworkError(true);

    }
    if (online) {
      createFieldNote({
        date: dayjs(new Date()).format(API_DATE_FORMAT),
        fieldId: fieldId || '',
        files: files || [],
        latitude: latitude || 0,
        longitude: longitude || 0,
        note: note || '',
        protocolId: protocolId || '',
        trialId,
        title: title ? title : null,
        stepId
      }).catch(() => {
        createSyncLaterNote()
      });
    } else {
      createSyncLaterNote()
    }
  };

  const handleOnSetLocation = () => {
    const centerPoint = getFieldCenterPoint(fieldBoundary);
    if (centerPoint) {
      setMarkers([{ ...centerPoint, draggable: true }]);
      setLatitude(centerPoint.latitude);
      setLongitude(centerPoint.longitude);
    }
  };

  const mapStyle = useSelector(selectFieldVisitWizardMapStyle);
  const { selectedImage } = useImagery({ fieldId, clientId, trialId, protocolId });

  const {
    displayModal,
    selectedLayers,
    display: mapOptionsModalDisplay
  } = useFieldMapOptionsModal({
    treatmentZones: treatmentZoneLegends,
    hasTreatmentZone: !!zoneGeojson,
    trialId: trialId || '',
    protocolId: protocolId || '',
    displayVisitTrialSelectWithSingleOption: true
  });

  return (
    <>
      <LoadingOverlayWrapper
        spinner
        active={createFieldNoteState.isLoading}
        text={t('create-field-note.saving-message')}
        styles={{
          wrapper: {
            height: '100vh'
          }
        }}
      >
        <Paper css={styles.container(theme)}>
          <ViewHeader title={t('create-field-note.title')} onGoBackBtnPress={handleGoBack} />
          <div css={styles.mapContainer}>
            <LoadingOverlayWrapper
              spinner
              active={getFieldTablesState.isLoading}
              styles={{ wrapper: styles.mapLoadingOverlayWrapper }}
              text={t('create-field-note.loading-message')}
            >
              <MapView
                showCurrentPosition={true}
                showFlyToCurrentLocation={true}
                fieldBoundary={fieldBoundary}
                markers={markers}
                onFocusMarkerLocationChange={onMarkerDragEnd}
                mapStyle={mapStyle || mapStyles.satellite}
                showSsurgo={selectedLayers.includes(MAP_LAYERS.SURGO)}
                showTreatmentZones={selectedLayers.includes(MAP_LAYERS.TREATMENT_ZONES)}
                showTrialArea={selectedLayers.includes(MAP_LAYERS.TRIAL_AREA)}
                soilGeojson={soilGeojson}
                trialAreaGeoJson={trialAreaGeoJson}
                zoneGeojson={zoneGeojson}
                selectedImage={selectedImage}
                fieldNotesMarkers={selectedLayers.includes(MAP_LAYERS.FIELD_NOTES) ? fieldNotesMarkers : []}
              />
            </LoadingOverlayWrapper>
          </div>
          <div css={styles.locationBtnContainer}>
            <Button css={styles.mapOptionBtn} variant="text" startIcon={<TuneIcon />} onClick={() => displayModal()}>
              {t('field-map.options-button')}
            </Button>
            <Button variant="text" size="small" onClick={() => handleOnSetLocation()}>
              {hasLocation ? t('create-field-note.edit-location-button') : t('create-field-note.add-location-button')}
            </Button>
          </div>
          <div css={styles.scrollableArea(theme)}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <TextField
                label={t('create-field-note.title-label')}
                value={title}
                type="text"
                onChange={e => setTitle(e.target.value)}
                inputProps={{
                  maxLength: 120
                }}
              />
            </FormControl>
            <FormControl fullWidth>
              <InputLabel variant="outlined" htmlFor="image-date-input">
                {t('create-field-note.step-label')}
              </InputLabel>
              <Select fullWidth value={stepId || ''} sx={{ mb: 2 }} label={t('create-field-note.step-label')} onChange={e => setStepId(e.target.value || null)}>
                <MenuItem value={''}>None</MenuItem>
                {steps?.map(step => {
                  return (
                    <MenuItem key={step.step_id} value={step.step_id}>
                      {step.label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <TextField
                label={t('create-field-note.note-label')}
                value={note}
                type="text"
                multiline
                minRows={4}
                onChange={onUpdateNote}
                inputProps={{
                  maxLength: 4000,
                  endAdornment: (
                    <InputAdornment position="end" variant="standard">
                      <IconButton aria-label="clear answer" onClick={onClearNote} edge="end">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </FormControl>
            <UploadPhoto hideSave={true} files={files} setFiles={setFiles} inputRef={inputRef} />
          </div>

          <div css={styles.actionsContainer(theme)}>
            <Button variant="text" onClick={handleGoBack} sx={{ textTransform: 'uppercase' }}>
              {t('general.cancel')}
            </Button>
            <Button
              css={styles.actionsBtn}
              variant="contained"
              onClick={() => onSaveNote()}
              disabled={markers.length === 0 || note === ''}
              sx={{ textTransform: 'uppercase' }}
            >
              {t('general.save')}
            </Button>
          </div>

          <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={showError} onClose={() => setShowError(false)}>
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setShowError(false);
                    handleGoBack();
                  }}
                >
                  {t('general.try-later')}
                </Button>
              }
            >
              <AlertTitle>{t('general.error')}</AlertTitle>
              {t('create-field-note.sync-error')}
            </Alert>
          </Snackbar>

          <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={showNetworkError} onClose={() => setShowNetworkError(false)}>
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setShowNetworkError(false);
                    handleGoBack();
                  }}
                >
                  {t('general.try-later')}
                </Button>
              }
            >
              <AlertTitle>{t('general.error')}</AlertTitle>
              {t('create-field-note.network-error')}
            </Alert>
          </Snackbar>
          {mapOptionsModalDisplay}
        </Paper>
      </LoadingOverlayWrapper>

      <Dialog open={geoLocationPermissionsAlertOpen} onClose={() => setGeoLocationPermissionsAlertOpen(true)}>
        <DialogTitle>{t('create-field-note.location-not-enabled-dialog-title')}</DialogTitle>
        <DialogContent>
          <DialogContentText css={styles.dialogText}>{t('create-field-note.location-not-enabled-dialog-text')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGeoLocationPermissionsAlertOpen(false)} variant="contained" autoFocus sx={{ textTransform: 'uppercase' }}>
            {t('general.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FieldNoteCreate;
